import { Route, Routes, Navigate } from "react-router-dom";
import InterestingWords from "./InterestingWords";
import NavBar from "./NavBar";
import Page from "./Page";
import SourceList from "./SourceList";
import SpotifyTopTracks from "./SpotifyTopTracks";
import TagManager from "./TagManager";
import SpotifyLogin from "./SpotifyLogin";
import SpotifyRedirect from "./SpotifyRedirect";
import DebugPage from "./DebugPage";
import NewChat from "./NewChat";
import { includeIf } from "../utils";
import { useCurrentDevice } from "../hooks";
import Login from "./Login";
import { useAuth } from "../context/AuthContext";
import PrivateRoutes from "./PrivateRoutes";
import {
  SOURCES_ROUTE,
  HOME_ROUTE,
  TAGS_ROUTE,
  INTERESTING_THINGS_ROUTE,
  NEW_CHAT_ROUTE,
  SPOTIFY_TOP_TRACKS_ROUTE,
  SPOTIFY_OAUTH_LOGIN_ROUTE,
  SPOTIFY_OAUTH_REDIRECT_ROUTE,
  DEBUG_PAGE_ROUTE,
  LOGIN_ROUTE,
} from "../routes";
import { useMemo, useState } from "react";
import { SourceBaseViewType } from "../api-generated";
import YouTubePage from "./YouTubePage";
import TextSource from "./TextSource";
import GoogledLyricsSource from "./GoogledLyricsSource";
import OpenAiChatSource from "./OpenAiChatSource";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import OptionsDropDown from "./OptionsDropdown";

const NAB_BAR_HEIGHT_IN_PX = 40;

const PlatzRoutes = () => {
  const { isPhone } = useCurrentDevice();
  const { isLoggedIn } = useAuth();

  const [isOptionsDropdownOpen, setIsOptionsDropdownOpen] =
    useState<boolean>(false);

  const [lastScrollY, setLastScrollY] = useState(0);
  const showNavbar = useMemo(() => {
    setLastScrollY(window.scrollY);
    if (window.scrollY < 20) {
      return true;
    } else if (window.scrollY > lastScrollY) {
      return false;
    } else {
      return true;
    }
  }, [window.scrollY]);

  const isInDev = process.env.NODE_ENV === "development";

  return (
    <>
      {isLoggedIn && (
        <NavBar
          showNavbar={showNavbar}
          items={[
            {
              labelOrIcon: "Sources",
              linkOrOnClick: SOURCES_ROUTE,
            },
            {
              labelOrIcon: isPhone ? "Things" : "Interesting Things",
              linkOrOnClick: INTERESTING_THINGS_ROUTE,
            },
            {
              labelOrIcon: "New Chat",
              linkOrOnClick: NEW_CHAT_ROUTE,
            },
            {
              labelOrIcon: "Spotify",
              linkOrOnClick: SPOTIFY_TOP_TRACKS_ROUTE,
            },
            ...includeIf(!isPhone, {
              labelOrIcon: "Tags",
              linkOrOnClick: TAGS_ROUTE,
            }),
            ...includeIf(isInDev, {
              labelOrIcon: "Debug",
              linkOrOnClick: DEBUG_PAGE_ROUTE,
            }),
            {
              labelOrIcon: faGear,
              linkOrOnClick: () => setIsOptionsDropdownOpen((prev) => !prev),
            },
          ]}
        />
      )}

      {isOptionsDropdownOpen && (
        <OptionsDropDown
          onLogout={() => setIsOptionsDropdownOpen(false)}
          navBarHeightInPx={NAB_BAR_HEIGHT_IN_PX}
        />
      )}

      <div
        style={{
          height: "100%",
          paddingTop: showNavbar ? `${NAB_BAR_HEIGHT_IN_PX}px` : "0px",
        }}
      >
        <Routes>
          <Route
            path={LOGIN_ROUTE}
            element={
              <Page
              //TODO: this check should also be done on the backend...?
              >
                {isLoggedIn ? <Navigate to={HOME_ROUTE} /> : <Login />}
              </Page>
            }
          />
          <Route element={<PrivateRoutes />}>
            <Route
              path={TAGS_ROUTE}
              element={
                <Page>
                  <TagManager />
                </Page>
              }
            />
            <Route
              path={`${SOURCES_ROUTE}/${SourceBaseViewType.youtube_transcript}/:id`}
              // TODO:hmm <SourceDetail/> itself renders <Page/>... is this weird?
              element={
                <Page>
                  <YouTubePage />
                </Page>
              }
            />
            <Route
              path={`${SOURCES_ROUTE}/${SourceBaseViewType.text}/:id`}
              element={
                <Page>
                  <TextSource />
                </Page>
              }
            />
            <Route
              path={`${SOURCES_ROUTE}/${SourceBaseViewType.googled_lyrics}/:id`}
              element={
                <Page>
                  <GoogledLyricsSource />
                </Page>
              }
            />
            <Route
              path={`${SOURCES_ROUTE}/${SourceBaseViewType.openai_chat}/:id`}
              element={
                <Page useDefaultStuff={false}>
                  <OpenAiChatSource />
                </Page>
              }
            />
            <Route
              path={SPOTIFY_TOP_TRACKS_ROUTE}
              element={
                <Page>
                  <SpotifyTopTracks />
                </Page>
              }
            />
            <Route
              path={SOURCES_ROUTE}
              element={
                <Page>
                  <SourceList />
                </Page>
              }
            />
            <Route
              path={NEW_CHAT_ROUTE}
              element={
                <Page>
                  <NewChat />
                </Page>
              }
            />
            <Route
              path={`${NEW_CHAT_ROUTE}/:query`}
              element={
                <Page>
                  <NewChat />
                </Page>
              }
            />
            <Route
              path={INTERESTING_THINGS_ROUTE}
              element={
                <Page>
                  <InterestingWords />
                </Page>
              }
            />
            <Route
              path={SPOTIFY_OAUTH_LOGIN_ROUTE}
              element={
                <Page>
                  <SpotifyLogin />
                </Page>
              }
            />
            <Route
              path={SPOTIFY_OAUTH_REDIRECT_ROUTE}
              element={
                <Page>
                  <SpotifyRedirect />
                </Page>
              }
            />
            {isInDev && (
              <Route
                //TODO: this could be more elegant
                path={DEBUG_PAGE_ROUTE}
                element={
                  <Page>
                    <DebugPage />
                  </Page>
                }
              />
            )}
            <Route path="*" element={<Navigate to={HOME_ROUTE} />} />
          </Route>
        </Routes>
      </div>
    </>
  );
};

export default PlatzRoutes;
